import { handleCopy } from "core/helpers/copy";
import { useTranslation } from "react-i18next";
import { Modal } from "shared/UI/Modal/Modal";
import { useGenerateRecoveryCodes } from "shared/core/hooks/useGenerateRecoveryCodes";
import { useNavigate } from "react-router-dom";
import { trackBtnClicks } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";
import { RecoveryCodesModalStatus } from "../../../interfaces";
import { useEffect, useState } from "react";
import { useStatusModal } from "shared/core/hooks/useStatusModal";
import { handleDownload } from "core/helpers/download";
import { useMobilePlatform } from "core/hooks/useMobilePlatform";
import { satisfies } from "semver";
import Button from "shared/UI/Buttons/Button";

interface RecoveryCodesModalProps {
  isVisible: boolean;
  onDismiss: () => void;
  status: RecoveryCodesModalStatus;
  code?: string;
}

export const RecoveryCodesModal = ({ isVisible, onDismiss, status, code }: RecoveryCodesModalProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "accountSecurity.twoFactorAuth.modal.recoveryCodes" });
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const { appVersion, isMobileApp } = useMobilePlatform();
  const { setBody, Modal: StatusModal } = useStatusModal();
  const { codes, loading, error } = useGenerateRecoveryCodes(isVisible, code);
  const allCodes = codes?.join("\n");
  const showDownloadBtn = (appVersion && satisfies(appVersion, t("appVersion"))) || !isMobileApp;

  const handleClose = () => {
    trackBtnClicks(EventNames.COMPLETE_RECOVERY_CODES_PROCESS);
    onDismiss();
    if (status === "new") {
      navigate("/account-security");
    }
  };

  const copyToClipboard = () => {
    handleCopy(allCodes);
    trackBtnClicks(EventNames.COPY_RECOVERY_CODES);
    setBody({
      data: t("copySuccess", { returnObjects: true }),
      status: "success",
    });
  };

  const handleFileDownload = async () => {
    setDownloading(true);
    trackBtnClicks(EventNames.DOWNLOAD_RECOVERY_CODES);

    const status = await handleDownload(allCodes, "Wisdom_Recovery_Codes.txt");
    const hasError = status === "error" || status === "cancelled";

    setBody({
      data: t(`download.${status}`, { returnObjects: true, defaultValue: "" }),
      status: hasError ? "error" : "success",
    });
    setDownloading(false);
  };

  useEffect(() => {
    if (!!error) {
      setBody({
        data: t("failure", { returnObjects: true, error: t(`errors.${error}`) }),
        status: "error",
        callback: onDismiss,
      });
    }
  }, [error, onDismiss, setBody, t]);

  if (!isVisible || loading) {
    return null;
  }

  return (
    <>
      {!error && (
        <Modal width="sm" close={() => {}}>
          <div data-testid="two-fa-recoverycode-modal" className="space-y-6 p-6">
            <div className="space-y-2">
              <h2 className="w-full p-0 text-center">{t(`${status}.title`)}</h2>
              <p className="w-full text-center">{t(`${status}.description`)}</p>
            </div>
            <div className="space-y-2">
              <p className="w-full text-center">{t(`title`)}</p>
              <div className="grid grid-cols-2 gap-2">
                {codes?.map((item: string) => (
                  <div key={item} className="bg-silver text-center p-2 rounded-sm">
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <Button text={t("cta.copy")} onClick={copyToClipboard} />
              {showDownloadBtn && (
                <Button
                  variant="secondary"
                  disabled={downloading}
                  text={t(`cta.${downloading ? "downloading" : "download"}`)}
                  onClick={handleFileDownload}
                />
              )}
              <Button variant="text" text={t("cta.finish")} onClick={handleClose} />
            </div>
          </div>
        </Modal>
      )}
      {StatusModal}
    </>
  );
};
